import { showToast, toast } from '@crowdex/ui/components/toaster';
import {
  changeConversation,
  deleteConversationForGood,
} from '@/lib/conversations/conversation-changes-client';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import type { SidebarThread } from '@/lib/sidebar-threads';

/**
 * A conversation's own actions from the sidebar's menu (D22), through the server
 * (`/api/conversations/:id`): rename, archive, back from the archive, delete. The list follows at
 * once (the server's `conversation.changed` follows too); an archived or deleted conversation
 * shown gives way to a new one. Archiving says so in a toast whose Undo brings it back, whichever
 * menu archived it (the sidebar's, the header's ⋮, the phone's drawer).
 */
export function createConversationServerActions({
  store,
  preview,
  resetConversation,
}: {
  store: DesktopShellStore;
  preview: boolean;
  resetConversation: () => void;
}) {
  const drop = (conversationId: string) => {
    const state = store.getState();
    state.setSyncedThreads((current) => current.filter((entry) => entry.id !== conversationId));
    state.setConversationCache((cache) => {
      const { [conversationId]: _gone, ...rest } = cache;
      return rest;
    });
    if (conversationId === state.chatId) resetConversation();
  };
  const unarchive = (conversationId: string) =>
    changeConversation(conversationId, { archived: false });
  return {
    async renameConversation(thread: SidebarThread, title: string): Promise<void> {
      if (preview) return;
      await changeConversation(thread.id, { title });
      const state = store.getState();
      state.setSyncedThreads((current) =>
        current.map((entry) => (entry.id === thread.id ? { ...entry, title } : entry)),
      );
      if (thread.id === state.chatId) state.setGeneratedConversationTitle(title);
    },
    async archiveConversation(thread: SidebarThread): Promise<void> {
      // The preview has no server: it says the same, and its Undo puts the row back.
      if (!preview) await changeConversation(thread.id, { archived: true });
      const shown = store.getState().syncedThreads.find((entry) => entry.id === thread.id);
      drop(thread.id);
      showToast({
        tone: 'info',
        title: 'Conversation archived',
        description: thread.title,
        action: {
          label: 'Undo',
          onSelect: () => {
            if (preview) {
              if (shown !== undefined)
                store
                  .getState()
                  .setSyncedThreads((current) =>
                    current.some((entry) => entry.id === shown.id) ? current : [shown, ...current],
                  );
              return;
            }
            void unarchive(thread.id).catch(() =>
              toast.error('The conversation could not be restored', thread.title),
            );
          },
        },
      });
    },
    /** Back from the archive: it joins the sidebar again (`conversation.changed`). */
    async unarchiveConversation(conversationId: string): Promise<void> {
      if (preview) return;
      await unarchive(conversationId);
    },
    async deleteConversation(thread: SidebarThread): Promise<void> {
      if (preview) return;
      await deleteConversationForGood(thread.id);
      drop(thread.id);
    },
  };
}
